import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t.return || t.return(); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import { emitter } from '../events/bridge-events-emitter';
import { MAX_MESSAGES_PER_RECEIVER, MAX_TOTAL_MESSAGES, MESSAGE_LIFETIME_IN_MILLIS, MESSAGE_TICK_TIME_IN_MILLIS } from './bridge-api-message-queue-constants';

/* eslint-disable no-console */

/**
 * An invocation which was unable to be sent to {@link receiverId} before, due to the receiver
 * not being in a ready state. It is maintained in the {@link BufferedInvocationQueue}, and flushed
 * once the receiver becomes ready. If a receiver is not ready within a time limit of
 * {@link MESSAGE_LIFETIME_IN_MILLIS}, then all messages tied to the receiver are dropped.
 */

/**
 * Maintains a queue of buffered invocations which were unable to send due to the receiver of the message
 * not being ready to receive them from the current bridge API instance.
 *
 * I.e., if JS context 1 mounts 10 seconds before JS context 2, and JS context 1 sends a message - it will
 * not be handle-able by JS context 2. Hence, we need to wait until JS context 2 is ready & then flush all
 * messages which JS context 1 was trying to send to JS context 2 *in the order they came*.
 *
 * But, also: We cannot hold onto messages forever. This is to avoid memory leaks, where we continue to generate
 * messages (e.g. via network streams), and an intended recipient is not reachable.
 *
 * {@link BufferedInvocationQueue} manages this set of requirements.
 */
export var BufferedInvocationQueue = /*#__PURE__*/function () {
  function BufferedInvocationQueue() {
    var _this = this;
    _classCallCheck(this, BufferedInvocationQueue);
    /**
     * A queue of messages keyed against a recipient client ID.
     *
     * @description Once the receiver is ready, we will flush all messages to them, IF they have not expired.
     */
    _defineProperty(this, "queue", new Map());
    /**
     * A lazy tick which is executed only if there are buffered messages, and cleans up a message after
     * {@link MESSAGE_LIFETIME_IN_MILLIS} have elapsed since the message was enqueued.
     *
     * @description NOTE: It is important to ensure there is only one interval which can run at any given time.
     * Too many timers can clog up the event loop and slow execution:
     */
    _defineProperty(this, "tick", undefined);
    /**
     * Starts an interval which does a full sweep every {@link MESSAGE_TICK_TIME_IN_MILLIS} to drop messages
     * which have gone beyond their lifetime.
     *
     * @description NOTE: The use of {@link setInterval} is deliberate here, as browsers will try their best
     * to continue executing {@link setInterval} callbacks even if a tab is unfocused. If the tab enters the
     * 'frozen' state in Chromium browsers, then execution is completely paused as per:
     * @see {@link https://developer.chrome.com/docs/web-platform/page-lifecycle-api}
     */
    _defineProperty(this, "startTick", function () {
      if (_this.tick) {
        return;
      }
      _this.tick = setInterval(function () {
        _this.checkLifetimeAndDrop();

        // Call the onTick callback if registered and there are buffered messages
        if (_this.onTickCallback && _this.queue.size > 0) {
          var pendingConnections = _this.getUniquePendingConnections();
          _this.onTickCallback(pendingConnections);
        }
      }, MESSAGE_TICK_TIME_IN_MILLIS);
    });
    /**
     * Stops the tick if there are no more messages to process.
     */
    _defineProperty(this, "stopTick", function () {
      if (_this.queue.size > 0) {
        return;
      }
      clearInterval(_this.tick);
      _this.tick = undefined;
    });
  }
  return _createClass(BufferedInvocationQueue, [{
    key: "enqueue",
    value:
    /**
     * Optional callback that gets called on every tick with unique senderId/receiverId pairs
     * for clients that are still waiting to connect.
     */

    /**
     * Optional callback that gets called when a message is dropped due to expiration or queue overflow.
     * @description Use this to perform any clean up activities relative to a buffered command.
     */

    /**
     * Buffers an invocation into a queue tied to the intended {@link receiverId}.
     *
     * @description Once the receiver is reachable, this message will be flushed alongside any other messages
     * which were previously enqueued against the provided {@link receiverId}.
     *
     * @param invocationId
     * @param senderId
     * @param receiverId
     * @param command
     * @param params
     */
    function enqueue(invocationId, senderId, receiverId, command, params) {
      var _this$queue$get;
      var queue = (_this$queue$get = this.queue.get(receiverId)) !== null && _this$queue$get !== void 0 ? _this$queue$get : [];
      var now = performance.now();
      var groupId = params.groupId;

      // check the receiver is not overwhelmed with too many pending messages; if it is, drop the oldest message
      if (queue.length >= MAX_MESSAGES_PER_RECEIVER) {
        console.warn("Message queue for receiver ".concat(receiverId, " is full, dropping oldest message"));
        var expired = queue.shift();
        if (expired) {
          var _this$onInvocationDro;
          (_this$onInvocationDro = this.onInvocationDropped) === null || _this$onInvocationDro === void 0 || _this$onInvocationDro.call(this, expired);
        }
      }

      // check if the overall queue isn't overwhelmed; if it is, drop the oldest message from each receiver
      if (this.messageCount >= MAX_TOTAL_MESSAGES) {
        console.warn('Total message queue is full, dropping oldest messages');
        this.checkAndDropOldestMessage();
      }

      // If this command has a groupId, reset the timer for all commands with the same groupId
      if (groupId) {
        this.resetGroupTimers(groupId, now);
      }
      queue.push({
        invocationId: invocationId,
        senderId: senderId,
        receiverId: receiverId,
        command: command,
        params: params,
        enqueuedAt: now
      });
      this.queue.set(receiverId, queue);
      emitter().emit({
        type: 'message-queued',
        messageCount: this.messageCount,
        messageId: invocationId,
        clientId: receiverId,
        reason: 'receiver-not-ready'
      });

      // now that we have >1 message (at least), start a tick which executes every N millis
      // and drop any messages which go beyond the lifetime limit
      this.startTick();
    }

    /**
     * Flush all messages associated with a speciifc receiver.
     *
     * @description Typically called once the receiver is marked as ready.
     *
     * @param receiverId unique identifier for the receiver
     */
  }, {
    key: "flush",
    value: function flush(receiverId) {
      var messages = this.queue.get(receiverId);

      // no messages found
      if (!messages) {
        return;
      }

      // we know we have a reference in the message list now - remove it
      this.deleteClientFromQueue(receiverId);

      // empty message list
      if (messages.length === 0) {
        return;
      }

      // emit queue flush event
      emitter().emit({
        type: 'queue-flushed',
        clientId: receiverId,
        messageCount: messages.length,
        reason: 'client-ready'
      });

      // return no-empty message list
      return messages;
    }

    /**
     * Returns the total number of messages across all receivers.
     * @returns count of messages across all buffered receivers
     */
  }, {
    key: "messageCount",
    get: function get() {
      return Array.from(this.queue.values()).reduce(function (sum, q) {
        return sum + q.length;
      }, 0);
    }

    /**
     * Registers a callback to be called on every tick with unique senderId/receiverId pairs
     * for clients that are still waiting to connect.
     *
     * @param callback Function to call with pending connection pairs
     */
  }, {
    key: "onTick",
    value: function onTick(callback) {
      this.onTickCallback = callback;
    }

    /**
     * Registers a callback to be called when a message is dropped due to expiration or queue overflow.
     *
     * @param callback Callback to invoke with the dropped message
     */
  }, {
    key: "onInvocationDroppped",
    value: function onInvocationDroppped(callback) {
      this.onInvocationDropped = callback;
    }

    /**
     * Drops all buffered messages for a specific client (both as sender and receiver).
     *
     * @param clientId unique identifier for the client whose messages should be dropped
     * @returns number of messages that were dropped
     */
  }, {
    key: "clear",
    value: function clear(clientId) {
      var _this2 = this;
      var droppedCount = 0;
      var toDelete = new Set();

      // remove all messages where this client is the receiver
      if (this.queue.has(clientId)) {
        var _this$queue$get2;
        var messages = (_this$queue$get2 = this.queue.get(clientId)) !== null && _this$queue$get2 !== void 0 ? _this$queue$get2 : [];
        if (messages.length > 0) {
          emitter().emit({
            type: 'queue-cleared',
            clientId: clientId,
            messageCount: this.messageCount - messages.length,
            reason: 'client-teardown'
          });
        }

        // notify before dropping messages
        if (this.onInvocationDropped) {
          var _iterator = _createForOfIteratorHelper(messages),
            _step;
          try {
            for (_iterator.s(); !(_step = _iterator.n()).done;) {
              var message = _step.value;
              this.onInvocationDropped(message);
            }
          } catch (err) {
            _iterator.e(err);
          } finally {
            _iterator.f();
          }
        }
        droppedCount += messages.length;

        // delete immediately - we do not want to process this queue below
        this.queue.delete(clientId);
      }

      // remove all messages where this client is the sender
      var _iterator2 = _createForOfIteratorHelper(this.queue),
        _step2;
      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var _step2$value = _slicedToArray(_step2.value, 2),
            receiverId = _step2$value[0],
            _messages = _step2$value[1];
          var messagesLength = _messages.length;
          var messagesToKeep = _messages.filter(function (message) {
            if (message.senderId === clientId) {
              // notify before dropping message
              if (_this2.onInvocationDropped) {
                _this2.onInvocationDropped(message);
              }
              return false;
            }
            return true;
          });
          var removedCount = messagesLength - messagesToKeep.length;
          if (removedCount > 0) {
            droppedCount += removedCount;
            if (messagesToKeep.length === 0) {
              toDelete.add(receiverId);
            } else {
              this.queue.set(receiverId, messagesToKeep);
            }
          }
        }

        // clean up empty queues and stop tick
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
      this.deleteClientsFromQueue(toDelete);
      return droppedCount;
    }

    /**
     * Drops a specific buffered message by its invocation ID.
     *
     * @param invocationId The unique identifier of the message to drop
     * @returns true if the message was found and dropped, false otherwise
     */
  }, {
    key: "cancel",
    value: function cancel(invocationId) {
      var _iterator3 = _createForOfIteratorHelper(this.queue),
        _step3;
      try {
        for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
          var _step3$value = _slicedToArray(_step3.value, 2),
            receiverId = _step3$value[0],
            messages = _step3$value[1];
          var messageIndex = messages.findIndex(function (msg) {
            return msg.invocationId === invocationId;
          });
          if (messageIndex !== -1) {
            var droppedMessage = messages[messageIndex];

            // notify before dropping message
            if (this.onInvocationDropped) {
              this.onInvocationDropped(droppedMessage);
            }

            // remove the message
            messages.splice(messageIndex, 1);

            // if no more messages for this receiver, clean up
            if (messages.length === 0) {
              this.deleteClientFromQueue(receiverId);
            }
            return true;
          }
        }
      } catch (err) {
        _iterator3.e(err);
      } finally {
        _iterator3.f();
      }
      return false;
    }

    /**
     * Cancels all buffered messages that match a specific group ID.
     *
     * @param groupId The group identifier of messages to cancel
     * @returns number of messages that were cancelled
     */
  }, {
    key: "cancelGroup",
    value: function cancelGroup(groupId) {
      var _this3 = this;
      var droppedCount = 0;
      var toDelete = new Set();
      var _iterator4 = _createForOfIteratorHelper(this.queue),
        _step4;
      try {
        for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
          var _step4$value = _slicedToArray(_step4.value, 2),
            receiverId = _step4$value[0],
            messages = _step4$value[1];
          var messagesToKeep = messages.filter(function (message) {
            if (message.params.groupId === groupId) {
              // notify before dropping message
              if (_this3.onInvocationDropped) {
                _this3.onInvocationDropped(message);
              }
              droppedCount++;
              return false;
            }
            return true;
          });
          if (messagesToKeep.length === 0) {
            toDelete.add(receiverId);
          } else if (messagesToKeep.length !== messages.length) {
            this.queue.set(receiverId, messagesToKeep);
          }
        }

        // clean up empty queues
      } catch (err) {
        _iterator4.e(err);
      } finally {
        _iterator4.f();
      }
      this.deleteClientsFromQueue(toDelete);
      return droppedCount;
    }

    /**
     * Clear all state relevant to the queue.
     */
  }, {
    key: "destroy",
    value: function destroy() {
      this.queue.clear();
      if (this.tick) {
        clearInterval(this.tick);
        this.tick = undefined;
      }
    }

    /**
     * Checks the lifetime of all messages in the queue and drops them if they have exceeded their
     * maximum allowed lifetime of {@link MESSAGE_LIFETIME_IN_MILLIS}.
     */
  }, {
    key: "checkLifetimeAndDrop",
    value: function checkLifetimeAndDrop() {
      var now = performance.now();
      var toDelete = new Set();

      // cull all messages for each receiver which have expired
      var _iterator5 = _createForOfIteratorHelper(this.queue),
        _step5;
      try {
        for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
          var _step5$value = _slicedToArray(_step5.value, 2),
            receiverId = _step5$value[0],
            messages = _step5$value[1];
          var needle = 0;

          // find the first message that has not expired (since messages are chronologically ordered)
          for (var idx = 0; idx < messages.length; idx++) {
            var waitTime = now - messages[idx].enqueuedAt;
            if (waitTime <= MESSAGE_LIFETIME_IN_MILLIS) {
              needle = idx;
              break;
            } else {
              needle = messages.length;
            }
          }

          // remove all expired messages in one sweep
          if (needle > 0) {
            // notify before dropping messages
            if (this.onInvocationDropped) {
              for (var _idx = 0; _idx < needle; _idx++) {
                this.onInvocationDropped(messages[_idx]);
              }
            }

            // remove expired messages
            messages.splice(0, needle);
          }

          // if we no longer have any messages, we should clear this entry entirely
          if (messages.length === 0) {
            toDelete.add(receiverId);
          }
        }

        // clean up all empty entries
      } catch (err) {
        _iterator5.e(err);
      } finally {
        _iterator5.f();
      }
      this.deleteClientsFromQueue(toDelete);
    }

    /**
     * Drops the oldest messages across all receivers when the total queue size is exceeded.
     */
  }, {
    key: "checkAndDropOldestMessage",
    value: function checkAndDropOldestMessage() {
      var oldest = Infinity;
      var receiver = null;
      var _iterator6 = _createForOfIteratorHelper(this.queue),
        _step6;
      try {
        for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
          var _step6$value = _slicedToArray(_step6.value, 2),
            receiverId = _step6$value[0],
            messages = _step6$value[1];
          if (messages.length > 0 && messages[0].enqueuedAt < oldest) {
            oldest = messages[0].enqueuedAt;
            receiver = receiverId;
          }
        }

        // drop the oldest message
      } catch (err) {
        _iterator6.e(err);
      } finally {
        _iterator6.f();
      }
      if (receiver) {
        var _this$queue$get3;
        var queue = (_this$queue$get3 = this.queue.get(receiver)) !== null && _this$queue$get3 !== void 0 ? _this$queue$get3 : [];
        queue.shift();

        // if no more messages - clean up
        if (queue.length === 0) {
          this.deleteClientFromQueue(receiver);
        }
      }
    }
  }, {
    key: "resetGroupTimers",
    value:
    /**
     * Resets the enqueuedAt time for all messages with the specified groupId across all receivers.
     * This ensures that when a new message with a groupId is enqueued, all messages in the same
     * group get their timer reset, allowing the entire group to stay alive for MESSAGE_LIFETIME_IN_MILLIS
     * after the last message in the group.
     *
     * @param groupId The group identifier to reset timers for
     * @param newTime The new enqueuedAt time to set for all messages in the group
     */
    function resetGroupTimers(groupId, newTime) {
      var _iterator7 = _createForOfIteratorHelper(this.queue.values()),
        _step7;
      try {
        for (_iterator7.s(); !(_step7 = _iterator7.n()).done;) {
          var messages = _step7.value;
          var _iterator8 = _createForOfIteratorHelper(messages),
            _step8;
          try {
            for (_iterator8.s(); !(_step8 = _iterator8.n()).done;) {
              var message = _step8.value;
              if (message.params.groupId === groupId) {
                message.enqueuedAt = newTime;
              }
            }
          } catch (err) {
            _iterator8.e(err);
          } finally {
            _iterator8.f();
          }
        }
      } catch (err) {
        _iterator7.e(err);
      } finally {
        _iterator7.f();
      }
    }

    /**
     * Extracts unique senderId/receiverId pairs from all buffered messages.
     * @returns Array of unique sender/receiver pairs that are still waiting to connect
     */
  }, {
    key: "getUniquePendingConnections",
    value: function getUniquePendingConnections() {
      var uniquePairs = new Map();
      var _iterator9 = _createForOfIteratorHelper(this.queue.values()),
        _step9;
      try {
        for (_iterator9.s(); !(_step9 = _iterator9.n()).done;) {
          var messages = _step9.value;
          var _iterator0 = _createForOfIteratorHelper(messages),
            _step0;
          try {
            for (_iterator0.s(); !(_step0 = _iterator0.n()).done;) {
              var message = _step0.value;
              var key = "".concat(message.senderId, ":").concat(message.receiverId);
              if (!uniquePairs.has(key)) {
                uniquePairs.set(key, {
                  senderId: message.senderId,
                  receiverId: message.receiverId
                });
              }
            }
          } catch (err) {
            _iterator0.e(err);
          } finally {
            _iterator0.f();
          }
        }
      } catch (err) {
        _iterator9.e(err);
      } finally {
        _iterator9.f();
      }
      return Array.from(uniquePairs.values());
    }

    /**
     * Safely deletes a single client from the queue and handles cleanup.
     *
     * @param clientId The client to remove from the queue
     * @private
     */
  }, {
    key: "deleteClientFromQueue",
    value: function deleteClientFromQueue(clientId) {
      this.queue.delete(clientId);
      this.stopTick();
    }

    /**
     * Safely deletes multiple clients from the queue and handles cleanup.
     *
     * @param clientIds Set of client IDs to remove from the queue
     * @private
     */
  }, {
    key: "deleteClientsFromQueue",
    value: function deleteClientsFromQueue(clientIds) {
      var _iterator1 = _createForOfIteratorHelper(clientIds),
        _step1;
      try {
        for (_iterator1.s(); !(_step1 = _iterator1.n()).done;) {
          var clientId = _step1.value;
          this.queue.delete(clientId);
        }
      } catch (err) {
        _iterator1.e(err);
      } finally {
        _iterator1.f();
      }
      this.stopTick();
    }
  }]);
}();